import { Box, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { FC, Fragment, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

// The subset of Markdown the analyst writes — headings, paragraphs, lists, tables, bold, italics and inline
// code, and links — rendered as React elements. No HTML is ever injected, so model output cannot run script.
// Links: a CASA path (/settings/…, /audiences/…) opens in the app; http(s) opens in a new tab; anything else is
// text. Image syntax is dropped: charts are shown as their own cards under the answer.

const INLINE = /(!?\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;
const LINK = /^(!?)\[([^\]]+)\]\(([^)\s]+)\)$/;

const link = (label: string, target: string, key: number): ReactNode => {
  if (/^\/(?!\/)/.test(target)) return <Link key={key} component={RouterLink} to={target} underline='hover' fontWeight={600}>{label}</Link>;
  if (/^https?:\/\//i.test(target)) {
    return <Link key={key} href={target} target='_blank' rel='noopener noreferrer' underline='hover' fontWeight={600}>{label}</Link>;
  }
  return <Fragment key={key}>{label}</Fragment>;
};

const inline = (text: string): ReactNode[] =>
  text.split(INLINE).filter(Boolean).map((part, i) => {
    const linked = LINK.exec(part);
    if (linked) return linked[1] ? null : link(linked[2], linked[3], i);
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) {
      return <Box key={i} component='code' sx={{ px: 0.5, borderRadius: 0.5, bgcolor: 'grey.100', fontSize: '0.9em' }}>{part.slice(1, -1)}</Box>;
    }
    if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) return <em key={i}>{part.slice(1, -1)}</em>;
    return <Fragment key={i}>{part}</Fragment>;
  });

const cells = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'table'; header: string[]; rows: string[][] };

export const parseBlocks = (source: string): Block[] => {
  const lines = source.replace(/\r/g, '').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) { blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2] }); i += 1; continue; }
    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const header = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim().startsWith('|')) { rows.push(cells(lines[i])); i += 1; }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }
    const bullet = /^\s*([-*•]|\d+[.)])\s+/;
    if (bullet.test(line)) {
      const ordered = /^\s*\d/.test(line);
      const items: string[] = [];
      while (i < lines.length && bullet.test(lines[i])) { items.push(lines[i].replace(bullet, '')); i += 1; }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }
    const paragraph: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4})\s/.test(lines[i]) && !bullet.test(lines[i]) && !lines[i].trim().startsWith('|')) {
      paragraph.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
  }
  return blocks;
};

const MarkdownText: FC<{ text: string }> = ({ text }) => (
  <Box sx={{ '& > *:first-of-type': { mt: 0 }, fontSize: 14, lineHeight: 1.6 }}>
    {parseBlocks(text).map((block, i) => {
      switch (block.kind) {
        case 'heading':
          return (
            <Typography key={i} variant={block.level <= 2 ? 'subtitle1' : 'subtitle2'} fontWeight={700} sx={{ mt: 2, mb: 0.75 }}>
              {inline(block.text)}
            </Typography>
          );
        case 'list':
          return (
            <Box key={i} component={block.ordered ? 'ol' : 'ul'} sx={{ pl: 2.5, my: 1 }}>
              {block.items.map((item, j) => <Typography key={j} component='li' variant='body2' sx={{ mb: 0.5 }}>{inline(item)}</Typography>)}
            </Box>
          );
        case 'table':
          return (
            <Box key={i} sx={{ my: 1.5, overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    {block.header.map((h, j) => (
                      <TableCell key={j} sx={{ fontSize: 12, fontWeight: 600, bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>{inline(h)}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {block.rows.map((row, r) => (
                    <TableRow key={r}>
                      {row.map((cell, c) => <TableCell key={c} sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{inline(cell)}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          );
        default:
          return <Typography key={i} variant='body2' sx={{ my: 1 }}>{inline(block.text)}</Typography>;
      }
    })}
  </Box>
);

export default MarkdownText;
